/**
 * Conversations Slice
 *
 * Manages conversation state including the current conversation ID,
 * conversation lists, and conversation-related loading states.
 */

import type { StateCreator } from 'zustand';
import i18n from '../../../../i18n';
import { chatService } from '../../../../shared/services/ChatService';
import { ChatSessionService } from '../../../../shared/services/ChatSessionService';
import { conversationService } from '../../../../shared/services/ConversationService';
import { AssistantMapper } from '../../../../shared/services/AssistantMapper';
import { botService } from '../../../../shared/services/BotService';
import { useBotStore } from '../../../../shared/store/botStore';
import { useCitationStore } from '../../../../shared/store/citationStore';
import type { Bot } from '../../../../shared/types/api.types';
import type {
  ChatMessage,
  FileAttachment,
} from '../../../../shared/types/chat.types';
import type { FileWithDisplayedSize } from '../../../../shared/types/files.types';
import { parseConversationTitle } from '../../../../shared/utils/mappers';
import type { ChatState, ConversationsSlice } from '../types';
import { currentDate } from '@/shared/utils/dateUtils';

/**
 * Initial state for the Conversations slice
 */
export const conversationsInitialState: Pick<
  ConversationsSlice,
  | 'conversationId'
  | 'conversations'
  | 'allConversations'
  | 'showAllConversations'
  | 'isDeletingConversation'
  | 'conversationsLoading'
  | 'isLoadingConversation'
> = {
  conversationId: null,
  conversations: [],
  allConversations: [],
  showAllConversations: false,
  isDeletingConversation: false,
  conversationsLoading: false,
  isLoadingConversation: false,
};

/**
 * Creates the conversations slice with conversation management actions.
 */
export const createConversationsSlice: StateCreator<
  ChatState,
  [
    ['zustand/devtools', never],
    ['zustand/persist', unknown],
    ['zustand/immer', never],
  ],
  [],
  ConversationsSlice
> = (set, get) => ({
  // Initial state
  ...conversationsInitialState,

  // Actions
  setConversationId: id => {
    // Reset multimodal flag when switching conversations (setting to null)
    set(
      {
        conversationId: id,
        hasMultimodalFilesInConversation:
          id === null ? false : get().hasMultimodalFilesInConversation,
      },
      false,
      'conversations/setId'
    );
  },

  fetchConversations: async () => {
    // Only show loading indicator on initial fetch (no data yet).
    // Subsequent refetches (tab switch, assistant switch) stay silent to avoid flicker.
    const isInitialFetch = get().allConversations.length === 0;
    if (isInitialFetch) {
      set({ conversationsLoading: true }, false, 'conversations/fetch:start');
    }

    try {
      const conversations = await chatService.getConversations();

      const allConversations = conversations.map(conv => ({
        id: conv._id,
        title: parseConversationTitle(conv.title),
      }));

      // Only update state if data actually changed to prevent unnecessary re-renders
      const current = get().allConversations;
      const hasChanged =
        current.length !== allConversations.length ||
        current.some(
          (conv, i) =>
            conv.id !== allConversations[i].id ||
            conv.title !== allConversations[i].title
        );

      if (hasChanged) {
        const { showAllConversations } = get();
        const displayConversations = showAllConversations
          ? allConversations
          : allConversations.slice(0, 5);

        set(
          {
            allConversations,
            conversations: displayConversations,
            conversationsLoading: false,
          },
          false,
          'conversations/fetch:success'
        );
      } else if (isInitialFetch) {
        set(
          { conversationsLoading: false },
          false,
          'conversations/fetch:unchanged'
        );
      }
    } catch (error) {
      console.error('fetchConversations error:', error);
      set(
        {
          allConversations: [],
          conversations: [],
          conversationsLoading: false,
        },
        false,
        'conversations/fetch:error'
      );
    }
  },

  loadConversation: async id => {
    // Hide citations when loading a different conversation
    const { hideCitations } = useCitationStore.getState();
    hideCitations();

    // Clear messages and set loading state immediately for instant UI feedback
    set(
      {
        messages: [],
        conversationId: id,
        isLoading: true,
        isLoadingConversation: true,
        error: null,
      },
      false,
      'conversations/load:start'
    );

    try {
      // STEP 1: Load conversation history first
      const messages = await chatService.getConversationHistory(id);

      // Check if this conversation uses a bot
      const hasBotConversation = messages.some(msg => {
        if (msg.type === 'ai' && msg.data.additional_kwargs) {
          const context = msg.data.additional_kwargs.context;
          return (
            Array.isArray(context) &&
            context.some(item => item.metadata?.store_id)
          );
        }
        return false;
      });

      if (hasBotConversation) {
        // Find the store_id from the first AI message with context
        let storeId: string | null = null;
        for (const msg of messages) {
          if (msg.type === 'ai' && msg.data.additional_kwargs?.context) {
            const contextItem = msg.data.additional_kwargs.context.find(
              item => item.metadata?.store_id
            );
            if (contextItem?.metadata?.store_id) {
              storeId = contextItem.metadata.store_id;
              break;
            }
          }
        }

        if (storeId) {
          const { availableBots, setSelectedBot } = useBotStore.getState();
          const matchingBot = availableBots.find(
            bot => bot.datastore && bot.datastore.includes(storeId)
          );

          if (matchingBot) {
            setSelectedBot(matchingBot);
          }
        }
        set(
          state => ({ ...state, chatSource: 'assistant' }),
          false,
          'conversations/load:setAssistantSource'
        );
      } else {
        useBotStore.getState().setSelectedBot(null);
        set(
          state => ({ ...state, chatSource: 'sidebar' }),
          false,
          'conversations/load:setSidebarSource'
        );
      }

      // Convert API messages to chat messages
      let lastUserMessageId: string | null = null;
      const chatMessages = messages.map((msg, langGraphIndex) => {
        const messageId = msg.data.id || crypto.randomUUID();

        // Convert uploaded_files to FileAttachment format
        let attachments: FileAttachment[] | undefined = undefined;
        if (
          msg.data.additional_kwargs?.uploaded_files &&
          msg.data.additional_kwargs.uploaded_files.length > 0
        ) {
          attachments = msg.data.additional_kwargs.uploaded_files.map(
            uploadedFile => {
              const fileSize =
                uploadedFile.file_size ??
                (uploadedFile as { filesize?: number }).filesize ??
                0;
              const mimeType =
                uploadedFile.content_type || 'application/octet-stream';
              const fileName = uploadedFile.filename || 'uploaded_file';

              const file = new File([], fileName, {
                type: mimeType,
              }) as FileWithDisplayedSize;
              file.displayedSize = fileSize;

              return {
                id: crypto.randomUUID(),
                name: fileName,
                size: fileSize,
                type: mimeType,
                url: uploadedFile.url,
                file,
              };
            }
          );
        }

        const chatMessage: ChatMessage = {
          id: messageId,
          text: msg.data.content,
          author: msg.type === 'human' ? 'user' : 'ai',
          timestamp: currentDate(),
          status: 'sent',
          additional_kwargs: msg.data.additional_kwargs,
          attachments: attachments,
          langGraphIndex,
        };

        if (msg.type === 'human') {
          lastUserMessageId = messageId;
        } else if (msg.type === 'ai') {
          if (lastUserMessageId) {
            chatMessage.responseTo = lastUserMessageId;
          }
          chatMessage.assistantName = '';
          if (msg.data.additional_kwargs?.deepResearchContext) {
            chatMessage.deepResearchContext =
              msg.data.additional_kwargs.deepResearchContext;
          }
        }

        return chatMessage;
      });

      // Check if any messages have attachments
      const hasAttachmentsInHistory = chatMessages.some(
        msg => msg.attachments && msg.attachments.length > 0
      );

      // STEP 2: Immediately render messages
      set(
        {
          messages: chatMessages,
          conversationId: id,
          isLoading: false,
          isLoadingConversation: false,
          isWebSearchEnabled: false,
          isDeepResearchEnabled: false,
          focusInputRequested: true,
          hasMultimodalFilesInConversation: hasAttachmentsInHistory,
        },
        false,
        'conversations/load:success'
      );

      // STEP 3: Load assistant details in background (non-blocking)
      loadAssistantDetailsInBackground(id, get, set);
    } catch (error) {
      console.error('[ChatStore] loadConversation error:', error);
      set(
        {
          error:
            error instanceof Error
              ? error.message
              : i18n.t('errors.chat.loadConversationFailed'),
          isLoading: false,
          isLoadingConversation: false,
        },
        false,
        'conversations/load:error'
      );
    }
  },

  retryConversation: async id => {
    const currentState = get();

    // Find the last user message
    const lastUserMessage = [...currentState.messages]
      .reverse()
      .find(m => m.author === 'user');

    if (!lastUserMessage) {
      return;
    }

    try {
      set(
        {
          isLoading: true,
          isStreaming: false,
          error: null,
        },
        false,
        'conversations/retry:start'
      );

      await chatService.retryConversation(id);
      await get().loadConversation(id);

      // Wait a bit to ensure conversation is fully loaded
      await new Promise(resolve => setTimeout(resolve, 100));

      const attachmentsToRetry =
        lastUserMessage.attachments && lastUserMessage.attachments.length > 0
          ? lastUserMessage.attachments
          : undefined;
      await get().sendMessage(lastUserMessage.text, attachmentsToRetry);
    } catch (error) {
      console.error('[ChatStore] retryConversation error:', error);
      set(
        {
          error:
            error instanceof Error
              ? error.message
              : 'Unable to retry, please try again.',
          isLoading: false,
          isStreaming: false,
        },
        false,
        'conversations/retry:error'
      );
      throw error;
    }
  },

  deleteConversation: async id => {
    try {
      set(
        { isDeletingConversation: true },
        false,
        'conversations/delete:start'
      );
      await chatService.deleteConversation(id);

      const { allConversations, conversationId, showAllConversations } = get();
      const updatedConversations = allConversations.filter(c => c.id !== id);
      const shouldCreateNew = conversationId === id;
      const displayConversations = showAllConversations
        ? updatedConversations
        : updatedConversations.slice(0, 5);

      set(
        {
          allConversations: updatedConversations,
          conversations: displayConversations,
          isDeletingConversation: false,
        },
        false,
        'conversations/delete:success'
      );

      if (shouldCreateNew) {
        get().createNewConversation();
      }

      return true;
    } catch (error) {
      set(
        {
          error:
            error instanceof Error
              ? error.message
              : i18n.t('errors.chat.deleteConversationFailed'),
          isDeletingConversation: false,
        },
        false,
        'conversations/delete:error'
      );
      return false;
    }
  },

  updateConversationTitle: async (id, title) => {
    const { allConversations, showAllConversations } = get();
    const previousConversations = [...allConversations];

    try {
      // Optimistic update using Immer
      set(
        state => {
          const conv = state.allConversations.find(c => c.id === id);
          if (conv) conv.title = title;
          // Update display conversations
          state.conversations = state.showAllConversations
            ? state.allConversations
            : state.allConversations.slice(0, 10);
        },
        false,
        'conversations/updateTitle:optimistic'
      );

      const success = await chatService.updateConversationTitle(id, title);

      if (!success) {
        // Rollback on failure
        set(
          {
            allConversations: previousConversations,
            conversations: showAllConversations
              ? previousConversations
              : previousConversations.slice(0, 10),
            error: i18n.t('errors.chat.updateTitleFailed'),
          },
          false,
          'conversations/updateTitle:rollback'
        );
        return false;
      }

      return true;
    } catch (error) {
      set(
        {
          allConversations: previousConversations,
          conversations: showAllConversations
            ? previousConversations
            : previousConversations.slice(0, 10),
          error:
            error instanceof Error
              ? error.message
              : i18n.t('errors.chat.updateTitleFailed'),
        },
        false,
        'conversations/updateTitle:error'
      );
      return false;
    }
  },

  createNewConversation: () => {
    set(
      {
        messages: [],
        conversationId: null,
        streamingMessage: null,
        error: null,
        hasMultimodalFilesInConversation: false,
      },
      false,
      'conversations/createNew'
    );
    get().fetchConversations();
  },

  toggleShowAllConversations: () => {
    const { showAllConversations, allConversations } = get();
    const newShowAll = !showAllConversations;
    const displayConversations = newShowAll
      ? allConversations
      : allConversations.slice(0, 5);

    set(
      {
        showAllConversations: newShowAll,
        conversations: displayConversations,
      },
      false,
      'conversations/toggleShowAll'
    );
  },
});

/**
 * Helper function to load assistant details in the background.
 * This is non-blocking and enriches the UI after messages are displayed.
 */
function loadAssistantDetailsInBackground(
  conversationId: string,
  get: () => ChatState,
  set: (
    partial: Partial<ChatState> | ((state: ChatState) => Partial<ChatState>),
    replace?: boolean,
    action?: string
  ) => void
): void {
  conversationService
    .getConversationDetail(conversationId)
    .then(conversationDetail => {
      // Check for locked model
      if (conversationDetail?.locked_model_detail) {
        const lockedModelDetail = conversationDetail.locked_model_detail;
        botService
          .getAllModels()
          .then(availableModels => {
            const matchingModel = availableModels.find(
              m => m.modelName === lockedModelDetail.modelName
            );

            if (matchingModel) {
              set(
                { currentModel: matchingModel },
                false,
                'conversations/load:autoSwitchLockedModel'
              );
            }
          })
          .catch(error => {
            console.error(
              '[ChatStore] Failed to fetch models for auto-switch:',
              error
            );
          });
      }

      if (conversationDetail?.assistant_detail) {
        const assistantDetail = conversationDetail.assistant_detail;
        set(
          state => ({ ...state, chatSource: 'assistant' }),
          false,
          'conversations/load:setAssistantSourceFromDetail'
        );

        // Update all AI messages with assistant name (skip for default assistants)
        const currentMessages = get().messages;
        const updatedMessages = currentMessages.map(msg => {
          if (msg.author === 'ai') {
            return {
              ...msg,
              assistantName: assistantDetail.is_default
                ? ''
                : assistantDetail.bot_name,
            };
          }
          return msg;
        });

        const bot = AssistantMapper.assistantDetailToBot(assistantDetail);

        const botItem = {
          id: String(assistantDetail.id),
          title: assistantDetail.bot_name,
          description: assistantDetail.description,
          division: assistantDetail.division || '',
          workspace: assistantDetail.workspace || '',
          isFavourite: false,
          createdBy: undefined,
        };

        ChatSessionService.enhanceBotForChat(botItem)
          .then(enhancedBot => {
            const botWithSession = {
              ...bot,
              sessionId: enhancedBot.sessionId,
              chatEndpoint: enhancedBot.chatEndpoint,
              capabilities: enhancedBot.capabilities,
              ...(enhancedBot.enhancedBotDetails || {}),
            } as Bot;

            set(
              {
                messages: updatedMessages,
                currentBot: botWithSession,
              },
              false,
              'conversations/load:assistantLoaded'
            );

            const { setSelectedBot } = useBotStore.getState();
            setSelectedBot(botWithSession, { skipClear: true });
          })
          .catch(() => {
            set(
              {
                messages: updatedMessages,
                currentBot: bot,
              },
              false,
              'conversations/load:assistantLoadedBasic'
            );

            const { setSelectedBot } = useBotStore.getState();
            setSelectedBot(bot, { skipClear: true });
          });
      } else {
        const currentState = get();
        if (currentState.chatSource !== 'sidebar') {
          set(
            state => ({ ...state, chatSource: 'sidebar' }),
            false,
            'conversations/load:setSidebarSourceFromDetail'
          );
        }
      }
    })
    .catch(() => {
      const currentState = get();
      if (currentState.chatSource !== 'sidebar') {
        set(
          state => ({ ...state, chatSource: 'sidebar' }),
          false,
          'conversations/load:setSidebarSourceFromError'
        );
      }
    });
}
